import styles from "./SegmentProgress.module.scss";

interface Props {
  current: number;
  total: number;
  /** When `true`, the current segment is rendered in an "active/running" colour. */
  highlightCurrent?: boolean;
  /** Optional accessible label override. */
  ariaLabel?: string;
}

/**
 * Compact segment-progress strip used in the operator console.
 *
 * Shows N cells (one per segment); cells before `current` are filled,
 * the current cell is highlighted (when `highlightCurrent` is set), and
 * the rest are empty. Falls back to "—" when there are no segments.
 */
export function SegmentProgress({
  current,
  total,
  highlightCurrent = false,
  ariaLabel,
}: Props) {
  if (total <= 0) {
    return (
      <span
        className={styles.root}
        aria-label={ariaLabel ?? "No segments"}
        data-testid="segment-progress-empty"
      >
        <span className={styles.label}>—</span>
      </span>
    );
  }

  const safeCurrent = Math.max(0, Math.min(total, current));
  const cells = Array.from({ length: total }, (_, idx) => {
    const segmentNumber = idx + 1;
    const isCurrent = highlightCurrent && segmentNumber === safeCurrent;
    const isFilled = !isCurrent && segmentNumber <= safeCurrent;
    const cls = isCurrent
      ? `${styles.cell} ${styles.cellCurrent}`
      : isFilled
        ? `${styles.cell} ${styles.cellFilled}`
        : styles.cell;
    return (
      <span
        key={segmentNumber}
        className={cls}
        data-cell-state={isCurrent ? "current" : isFilled ? "filled" : "empty"}
      />
    );
  });

  return (
    <span
      className={styles.root}
      aria-label={ariaLabel ?? `Segment ${safeCurrent} of ${total}`}
      data-testid="segment-progress"
      data-current={safeCurrent}
      data-total={total}
    >
      <span className={styles.bar} aria-hidden>
        {cells}
      </span>
      <span className={styles.label}>
        {safeCurrent}/{total}
      </span>
    </span>
  );
}
